// src/components/CustomizeMembership/MembershipCheckout.js

import React, { useState, useEffect } from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { auth } from "../../firebaseConfig";

import { calculateShippingCost } from "../utils/shippingCalculator";
import { haversineDistance } from "../utils/geoUtils";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./MembershipCheckout.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PROJECT_KEY = "ijrr_user";
const BASE_PRICE = 19.99;

export default function MembershipCheckout({ rawAssetFile, finalCanvasBlob, selectedFlagColor }) {
  const [userAddress, setUserAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    mobile: ""
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  const [deliveryFee, setDeliveryFee] = useState(null);
  const [estimatedDelivery, setEstimatedDelivery] = useState("");
  const [displayAddress, setDisplayAddress] = useState("");
  const [isCalculating, setIsCalculating] = useState(false); // Used for API loading too

  const [showPayPal, setShowPayPal] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [{ isPending }] = usePayPalScriptReducer();

  // Store the ID returned by the backend to update it later (to 'paid')
  const [createdMembershipId, setCreatedMembershipId] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        fetchCustomerData(firebaseUser.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchCustomerData = async (uid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/customers/${uid}`);
      if (!response.ok) throw new Error("Failed to fetch customer data");
      const data = await response.json();
      const customer = data.customer;
      setUserAddress({
        firstName: customer.first_name || "",
        lastName: customer.last_name || "",
        email: customer.email || "",
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        country: customer.country || "",
        postalCode: customer.postal_code || "",
        mobile: customer.mobile || ""
      });
      localStorage.setItem(PROJECT_KEY, JSON.stringify(customer));
    } catch (error) {
      toast.error("Failed to load user data", { autoClose: 2000 });
    }
  };

  const handleInputChange = (e) => {
    setUserAddress({ ...userAddress, [e.target.name]: e.target.value });
  };

  const editDeliveryInfo = () => {
    setIsEditing(true);
    setShowPayPal(false);
    setIsPaying(false);
    // Note: We keep createdMembershipId. If they edit and save again, we update the existing record.
  };

  const saveDeliveryInfo = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/customers/${auth.currentUser.uid}/update-address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: userAddress.address,
          city: userAddress.city,
          state: userAddress.state,
          country: userAddress.country,
          postal_code: userAddress.postalCode
        })
      });
      if (!response.ok) throw new Error("Failed to update address");
      toast.success("Address updated successfully!", { autoClose: 2000 });
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to save changes", { autoClose: 2000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleProceedToPay = async () => {
    // 1. Validation
    const missingFields = [];
    if (!userAddress.address.trim()) missingFields.push("Address");
    if (!userAddress.state.trim()) missingFields.push("State");
    if (!userAddress.country.trim()) missingFields.push("Country");
    if (!userAddress.postalCode.trim()) missingFields.push("Postal Code");

    if (missingFields.length > 0) {
      setValidationErrors(missingFields);
      setIsValidationModalOpen(true);
      return;
    }

    if (!rawAssetFile || !finalCanvasBlob) {
      toast.error("Design files are missing. Please refresh and try again.");
      return;
    }

    setIsCalculating(true);
    setDeliveryFee(null);
    setEstimatedDelivery("");
    setDisplayAddress("");
    setShowPayPal(false); // Hide until successful API creation

    try {
      // 2. Calculate Shipping (Nominatim)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${userAddress.postalCode}&countrycodes=mx,us&format=json`
      );
      if (!response.ok) throw new Error("Failed to fetch location data");
      const data = await response.json();
      if (data.length === 0) {
        toast.error("Invalid postal code. Please check and try again.", { autoClose: 2000 });
        setIsCalculating(false);
        return;
      }
      const { lat, lon, display_name } = data[0];
      const pvLat = 20.6534;
      const pvLon = -105.2253;
      const distanceKm = haversineDistance(pvLat, pvLon, parseFloat(lat), parseFloat(lon));
      const { cost: shippingCost, estimatedDelivery: deliveryTime } = calculateShippingCost(distanceKm, 1);

      const totalCost = BASE_PRICE + shippingCost;

      // 3. Create or Update Membership via API
      const formData = new FormData();
      formData.append('firebase_uid', auth.currentUser.uid);
      formData.append('flag_color', selectedFlagColor || 'white');
      
      // Files
      formData.append('main_asset', rawAssetFile);
      formData.append('flag_design', finalCanvasBlob, 'flag_design.png');
      
      // Address
      formData.append('address', userAddress.address);
      formData.append('city', userAddress.city);
      formData.append('state', userAddress.state);
      formData.append('country', userAddress.country);
      formData.append('postal_code', userAddress.postalCode);
      
      // Costs
      formData.append('base_price', BASE_PRICE);
      formData.append('delivery_fee', shippingCost);
      formData.append('total', totalCost);
      
      // Statuses
      formData.append('payment_status', 'pending');
      formData.append('cc_last_4', '0000'); // Placeholder until payment

      let apiUrl = `${API_BASE_URL}/memberships/purchase`;
      
      // If we already created one in this session, update it instead of creating duplicate
      if (createdMembershipId) {
        apiUrl = `${API_BASE_URL}/memberships/update`;
        formData.append('membership_id', createdMembershipId);
      }

      const apiResponse = await fetch(apiUrl, {
        method: 'POST',
        body: formData // Content-Type header is automatic with FormData
      });

      const apiResult = await apiResponse.json();

      if (!apiResponse.ok) {
        throw new Error(apiResult.message || "Failed to create membership record.");
      }

      // 4. Success: Store ID and Show UI
      if (apiResult.membership_id) {
          setCreatedMembershipId(apiResult.membership_id);
      } else if (apiResult.membership && apiResult.membership.id) {
          setCreatedMembershipId(apiResult.membership.id);
      }

      setDeliveryFee(shippingCost);
      setEstimatedDelivery(deliveryTime);
      setDisplayAddress(display_name);
      
      toast.success("Order prepared. Please proceed to payment.");
      setIsCalculating(false);

    } catch (error) {
      console.error(error);
      toast.error(`Error: ${error.message}`, { autoClose: 3000 });
      setIsCalculating(false);
    }
  };

  // 5. Finalize Payment: Update status to 'paid'
  const handleOnApprove = async (details) => {
    if (!createdMembershipId) {
        toast.error("Order ID missing. Please contact support.");
        return;
    }

    try {
        const formData = new FormData();
        formData.append('firebase_uid', auth.currentUser.uid);
        formData.append('membership_id', createdMembershipId);
        formData.append('payment_status', 'paid');
        formData.append('cc_last_4', 'PYPL'); // Or extract actual last 4 from details if available

        // We use the update endpoint to set it to paid
        const response = await fetch(`${API_BASE_URL}/memberships/update`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error("Failed to update payment status");
        }
        
        toast.success(`Payment Successful! Membership #${createdMembershipId} is now active.`);
        // Redirect or clear state here if necessary
    } catch (error) {
        console.error("Payment update error:", error);
        toast.error("Payment successful but failed to update system. Please contact support.");
    }
  };

  const total = BASE_PRICE + (deliveryFee || 0);

  return (
    <section className="membership-checkout-section space-customize-membership-checkout">
      <ToastContainer position="top-right" theme="light" />
      <div className="custom-container">
        <div className="row checkout-section">
          <div className="col-md-6 checkout-box">
            <h3>Delivery Information</h3>

            <form className="delivery-form">
              <div className="row">
                <div className="col-md-6 mb-14">
                  <label className="form-label">First Name:</label>
                  <input type="text" className="form-control" value={userAddress.firstName} disabled />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">Last Name:</label>
                  <input type="text" className="form-control" value={userAddress.lastName} disabled />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">Email:</label>
                  <input type="email" className="form-control" value={userAddress.email} disabled />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">Address:</label>
                  <input type="text" className="form-control" name="address" value={userAddress.address} onChange={handleInputChange} disabled={!isEditing} placeholder="Enter address..." />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">City:</label>
                  <input type="text" className="form-control" name="city" value={userAddress.city} onChange={handleInputChange} disabled={!isEditing} placeholder="Enter city..." />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">State:</label>
                  <input type="text" className="form-control" name="state" value={userAddress.state} onChange={handleInputChange} disabled={!isEditing} placeholder="Enter state..." />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">Country:</label>
                  <input type="text" className="form-control" name="country" value={userAddress.country} onChange={handleInputChange} disabled={!isEditing} placeholder="Enter country..." />
                </div>
                <div className="col-md-6 mb-14">
                  <label className="form-label">Postal Code:</label>
                  <input type="text" className="form-control" name="postalCode" value={userAddress.postalCode} onChange={handleInputChange} disabled={!isEditing} placeholder="Enter postal code..." />
                </div>

                <div className="col-12 d-flex justify-content-between align-items-center mt-10">
                  <button type="button" className={`btn ${isEditing ? "btn-success btn-save" : "btn-secondary btn-edit"}`} onClick={isEditing ? saveDeliveryInfo : editDeliveryInfo}>
                    <i className={`fa ${isEditing ? "fa-save" : "fa-pencil-alt"}`}></i> {isEditing ? "Save" : "Edit"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary btn-proceed"
                    disabled={isEditing || isCalculating}
                    onClick={handleProceedToPay}
                  >
                     {isCalculating ? (
                         <span><i className="fa fa-spinner fa-spin"></i> Processing...</span>
                    ) : (
                         <span><i className="fa fa-clipboard-check"></i> Review & Proceed</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {(deliveryFee !== null && !isCalculating && !isEditing) && (
            <div className="col-md-6 checkout-box animate__animated animate__fadeIn">
              <h3>Order Summary</h3>
              <div className="summary-details">
                <p><strong>Base Price:</strong> ${BASE_PRICE}</p>
                <p><strong>Delivery Fee:</strong> ${deliveryFee.toFixed(2)}</p>
              </div>
              <hr className="summary-divider" />
              <p className="total-price-summary"><strong>Total:</strong> ${total.toFixed(2)}</p>
              
              <div className="delivery-info">
                  <p><i className="fa fa-calendar"></i> <strong>Estimated Delivery Date:</strong> {estimatedDelivery}</p>
                  <p><i className="fa fa-map-marker"></i> <strong>Delivery Address:</strong> {userAddress.address}, {userAddress.city}, {userAddress.state}</p>
                  <p><i className="fa fa-map-pin"></i> <strong>Location:</strong> {displayAddress}</p>
              </div>
              
              {!isPaying && (
                <button className="btn btn-checkout" onClick={() => { setShowPayPal(true); setIsPaying(true); }}>
                  <i className="fa fa-credit-card"></i> Submit Order & Pay
                </button>
              )}
              
              {showPayPal && (
                <div className="paypal-container mt-3">
                  {isPending ? <p>Loading PayPal...</p> : (
                    <PayPalButtons
                      style={{ layout: "vertical" }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [
                            {
                              amount: {
                                currency_code: "USD",
                                value: total.toFixed(2),
                              },
                            },
                          ],
                        });
                      }}
                      onApprove={async (data, actions) => {
                        return actions.order.capture().then((details) => {
                          handleOnApprove(details);
                        });
                      }}
                      onError={(err) => {
                        toast.error("Payment failed, please try again.");
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isValidationModalOpen && (
        <div className="error-modal" onClick={() => setIsValidationModalOpen(false)}>
          <div className="error-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setIsValidationModalOpen(false)}>×</button>
            <h3>Missing Required Fields</h3>
            <p>Please fill in the following required fields:</p>
            <ul>
              {validationErrors.map((error, i) => <li key={i}><strong>{error}</strong></li>)}
            </ul>
            <button className="btn-primary" onClick={() => setIsValidationModalOpen(false)}>OK</button>
          </div>
        </div>
      )}
    </section>
  );
}